import { useMemo, useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { transformAncet } from "@/features/chats";
import { useAdminChats } from "../model/AdminChatsContext";
import { useAdminChatOutlet } from "./AdminChatUserLayout";
import { useAdminRefreshSamplesMutation, useEditChatMutation } from "@/store/usersApi";
import { Preloader } from "@/components";

import styles from "./AdminChatSummaryPage.module.scss";

export const AdminChatSummaryPage = () => {
  const navigate = useNavigate();
  const { userId, profile } = useAdminChatOutlet();
  const { getCachedMessages, initData, updateChatStage, updateChatComment, getChat } = useAdminChats();
  
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [regeneratePrompt, setRegeneratePrompt] = useState("");
  const [adminRefreshSamples, { isLoading: isRegenerating }] = useAdminRefreshSamplesMutation();
  
  // Состояния для редактирования stage и comment
  const [editingStage, setEditingStage] = useState(false);
  const [editingComment, setEditingComment] = useState(false);
  const [newStage, setNewStage] = useState(profile.stage || "");
  const [newComment, setNewComment] = useState(profile.coment || "");
  const [isCustomStage, setIsCustomStage] = useState(false);
  const stageInputRef = useRef<HTMLInputElement>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const commentContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const [editChat, { isLoading: isUpdating }] = useEditChatMutation();
  
  // Получаем уникальные стадии из всех чатов
  const { chats } = useAdminChats();
  const availableStages = Array.from(
    new Set(chats.map((chat) => chat.stage).filter(Boolean) as string[])
  ).sort();
  
  useEffect(() => {
    // Обновляем только если не редактируем
    if (!editingStage) {
      setNewStage(profile.stage || "");
    }
    if (!editingComment) {
      setNewComment(profile.coment || "");
    }
  }, [profile.stage, profile.coment, editingStage, editingComment]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editingStage &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        stageInputRef.current &&
        !stageInputRef.current.contains(event.target as Node)
      ) {
        setEditingStage(false);
        setIsCustomStage(false);
        setNewStage(profile.stage || "");
      }
      if (
        editingComment &&
        commentContainerRef.current &&
        !commentContainerRef.current.contains(event.target as Node)
      ) {
        setEditingComment(false);
        setNewComment(profile.coment || "");
      }
    };

    if (editingStage || editingComment) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [editingStage, editingComment, profile.stage, profile.coment]);
  
  const handleStageSave = async () => {
    if (!initData) {
      toast.error("Ошибка: отсутствуют данные инициализации");
      return;
    }
    
    if (newStage.trim() === profile.stage) {
      setEditingStage(false);
      setIsCustomStage(false);
      return;
    }
    
    try {
      await editChat({
        user_id: userId,
        data: { stage: newStage.trim() },
      }).unwrap();
      
      await updateChatStage(userId, newStage.trim());
      // Обновляем профиль из кэша
      await getChat(userId);
      setEditingStage(false);
      setIsCustomStage(false);
      toast.success("Статус обновлен");
    } catch (error) {
      console.error("Ошибка обновления статуса:", error);
      toast.error("Ошибка при обновлении статуса");
      setNewStage(profile.stage || "");
    }
  };
  
  const handleCommentSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!initData) {
      toast.error("Ошибка: отсутствуют данные инициализации");
      return;
    }
    
    if (isUpdating) {
      return;
    }
    
    const currentComment = (profile.coment || "").trim();
    const trimmedNewComment = (newComment || "").trim();
    
    // Если комментарий не изменился, просто закрываем редактирование
    if (trimmedNewComment === currentComment) {
      setEditingComment(false);
      return;
    }
    
    try {
      await editChat({
        user_id: userId,
        data: { coment: trimmedNewComment },
      }).unwrap();
      
      // Обновляем комментарий в кэше напрямую
      updateChatComment(userId, trimmedNewComment);
      
      // Обновляем профиль из сервера для получения актуальных данных (включая updated_at)
      await getChat(userId);
      
      setEditingComment(false);
      toast.success("Комментарий обновлен");
    } catch (error) {
      console.error("Ошибка обновления комментария:", error);
      toast.error("Ошибка при обновлении комментария");
      setNewComment(profile.coment || "");
    }
  };
  
  const formattedUpdatedAt = profile.updated_at
    ? (() => {
        const date = new Date(profile.updated_at);
        const options: Intl.DateTimeFormatOptions = {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        };
        return date.toLocaleDateString("ru-RU", options);
      })()
    : null;

  const messagesCount = getCachedMessages(userId)?.length ?? "—";

  const ancetView = useMemo(
    () => transformAncet(profile.ancet ?? []),
    [profile.ancet],
  );
  const ancetPreview = useMemo(() => ancetView.slice(0, 6), [ancetView]);

  const trainingsSummary = useMemo(() => {
    if (!profile.trainings?.length) {
      return "Тренировок пока нет";
    }

    const upcoming = profile.trainings.filter((training) => {
      return training.status === 'wait' || training.status === null || training.status === undefined;
    }).length;

    return `${profile.trainings.length} всего · ${upcoming} предстоящих`;
  }, [profile.trainings]);

  const equipmentsSummary = useMemo(() => {
    if (!profile.equipments?.length) {
      return "Оборудование не указано";
    }

    const equipmentCount = new Set(
      profile.equipments.map((eq) => eq.equipment_id),
    ).size;

    return `${equipmentCount} ${equipmentCount === 1 ? "тип" : "типов"} оборудования`;
  }, [profile.equipments]);

  const handleRegenerateClick = () => {
    setShowRegenerateInput(true);
  };

  const handleRegenerateCancel = () => {
    setShowRegenerateInput(false);
    setRegeneratePrompt("");
  };

  const handleRegenerateConfirm = async () => {
    if (!initData) {
      toast.error("Ошибка: отсутствуют данные инициализации");
      return;
    }

    try {
      await adminRefreshSamples({
        userId,
        userMsg: regeneratePrompt || undefined,
      }).unwrap();
      
      toast.success("Программа перегенерирована успешно!");
      setShowRegenerateInput(false);
      setRegeneratePrompt("");
    } catch (error) {
      console.error("Ошибка перегенерации программы:", error);
      toast.error("Ошибка при перегенерации программы");
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.profileCard}>
        <div className={styles.avatar}>
          {profile.fullname ? profile.fullname.charAt(0).toUpperCase() : "?"}
        </div>
        <div className={styles.profileInfo}>
          <span className={styles.profileName}>{profile.fullname}</span>
          {profile.username && (
            <span className={styles.profileUsername}>@{profile.username}</span>
          )}
        </div>
      </div>

      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate("/admin/chats")}
      >
        ← Назад к чатам
      </button>

      {/* Статус и комментарий */}
      <div className={styles.statusSection}>
        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>Статус:</span>
          {editingStage ? (
            <div className={styles.statusEditContainer}>
              {!isCustomStage ? (
                <div className={styles.statusDropdown} ref={dropdownRef}>
                  {availableStages.map((stage) => (
                    <button
                      key={stage}
                      type="button"
                      className={styles.statusOption}
                      onClick={() => {
                        setNewStage(stage);
                        setIsCustomStage(false);
                      }}
                    >
                      {stage}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={styles.statusOptionCustom}
                    onClick={() => {
                      setIsCustomStage(true);
                      setTimeout(() => {
                        stageInputRef.current?.focus();
                      }, 0);
                    }}
                  >
                    + Ввести новый статус
                  </button>
                </div>
              ) : (
                <input
                  ref={stageInputRef}
                  type="text"
                  value={newStage}
                  onChange={(e) => setNewStage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleStageSave();
                    } else if (e.key === "Escape") {
                      setEditingStage(false);
                      setIsCustomStage(false);
                      setNewStage(profile.stage || "");
                    }
                  }}
                  placeholder="Введите новый статус"
                  className={styles.statusInput}
                  autoFocus
                />
              )}
              <div className={styles.statusActions}>
                <button
                  type="button"
                  className={styles.statusSaveButton}
                  onClick={handleStageSave}
                  disabled={isUpdating}
                >
                  ✓
                </button>
                <button
                  type="button"
                  className={styles.statusCancelButton}
                  onClick={() => {
                    setEditingStage(false);
                    setIsCustomStage(false);
                    setNewStage(profile.stage || "");
                  }}
                  disabled={isUpdating}
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.statusDisplay}>
              <span
                className={styles.statusValue}
                onClick={() => setEditingStage(true)}
                title="Нажмите для изменения статуса"
              >
                {profile.stage || "Без статуса"}
              </span>
            </div>
          )}
        </div>

        <div className={styles.statusItem}>
          <span className={styles.statusLabel}>Комментарий:</span>
          {editingComment ? (
            <div
              ref={commentContainerRef}
              className={styles.commentEditContainer}
              onClick={(e) => e.stopPropagation()}
            >
              <textarea
                ref={commentInputRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Введите комментарий"
                className={styles.commentInput}
                rows={3}
                autoFocus
              />
              <div className={styles.statusActions} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className={styles.statusSaveButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleCommentSave(e);
                  }}
                  disabled={isUpdating}
                >
                  ✓
                </button>
                <button
                  type="button"
                  className={styles.statusCancelButton}
                  onClick={() => {
                    setEditingComment(false);
                    setNewComment(profile.coment || "");
                  }}
                  disabled={isUpdating}
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.commentDisplay}>
              <span
                className={styles.commentValue}
                onClick={() => setEditingComment(true)}
                title="Нажмите для изменения комментария"
              >
                {profile.coment || "Нет комментария"}
              </span>
            </div>
          )}
        </div>

        {formattedUpdatedAt && (
          <div className={styles.statusItem}>
            <span className={styles.statusLabel}>Обновлено:</span>
            <span className={styles.updatedAt}>{formattedUpdatedAt}</span>
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.requestsCard}
        onClick={() => navigate("chat")}
      >
        <span className={styles.requestsTitle}>Чат с клиентом</span>
        <span className={styles.requestsBadge}>{messagesCount}</span>
        <span className={styles.requestsArrow}>→</span>
      </button>
      <button
        type="button"
        className={styles.requestsCard}
        onClick={() => navigate("logs")}
      >
        <span className={styles.requestsTitle}>Логи клиента</span>
        {/* <span className={styles.requestsBadge}>{logsCount}</span> */}
        <span className={styles.requestsArrow}>→</span>
      </button>

      <div className={styles.sections}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Анкета клиента</h3>
            <Link to="ancet" className={styles.sectionLink}>
              Посмотреть полностью
            </Link>
          </div>

          <ul className={styles.sectionList}>
            {ancetPreview.map((item) => (
              <li key={item.questNum}>
                <span className={styles.sectionLabel}>{item.title}</span>
                <span className={styles.sectionValue}>{item.value}</span>
              </li>
            ))}
            {!ancetPreview.length && (
              <li className={styles.sectionEmpty}>Анкета пока пустая</li>
            )}
          </ul>
        </div>

        <button
          type="button"
          className={styles.trainingsCard}
          onClick={() => navigate("trainings-program")}
        >
          <div className={styles.trainingsHeader}>
            <h3>Программа тренировок</h3>
            <span className={styles.trainingsArrow}>→</span>
          </div>
          <span className={styles.trainingsSummary}>{trainingsSummary}</span>
          <span className={styles.trainingsSubtitle}>
            Все тренировки: завершённые и предстоящие
          </span>
        </button>

        <button
          type="button"
          className={styles.trainingsCard}
          onClick={() => navigate("equipments")}
        >
          <div className={styles.trainingsHeader}>
            <h3>Оборудование клиента</h3>
            <span className={styles.trainingsArrow}>→</span>
          </div>
          <span className={styles.trainingsSummary}>{equipmentsSummary}</span>
          <span className={styles.trainingsSubtitle}>
            Доступное оборудование в зале клиента
          </span>
        </button>

        {/* Кнопка перегенерации программы */}
        <div className={styles.section}>
          {!showRegenerateInput ? (
            <button
              type="button"
              onClick={handleRegenerateClick}
              disabled={isRegenerating}
              className={styles.regenerateButton}
            >
              {isRegenerating ? (
                <div className={styles.regenerateLoader}>
                  <Preloader />
                  <span>Перегенерация программы...</span>
                </div>
              ) : (
                "Перегенерировать программу"
              )}
            </button>
          ) : (
            <div className={styles.regenerateInputContainer}>
              <label className={styles.regenerateLabel}>
                Введите промпт для перегенерации программы:
              </label>
              <textarea
                value={regeneratePrompt}
                onChange={(e) => setRegeneratePrompt(e.target.value)}
                placeholder="Опишите, что нужно изменить в программе..."
                className={styles.regenerateTextarea}
                rows={4}
              />
              <div className={styles.regenerateActions}>
                <button
                  type="button"
                  onClick={handleRegenerateCancel}
                  className={styles.regenerateCancelButton}
                  disabled={isRegenerating}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateConfirm}
                  className={styles.regenerateConfirmButton}
                  disabled={isRegenerating}
                >
                  {isRegenerating ? "Отправка..." : "Подтвердить"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

